'use client'

import React, { useState, useEffect } from 'react'
import styles from './TerminalTabs.module.css'
import { useGrowwPositions } from './hooks/useGrowwPositions'
import { GrowwConnector } from '../GrowwConnector'
import { Position } from '../types'

/**
 * @uiComponent
 * @name Positions
 * @description The Positions component displays active trading positions on the Groww platform.
 * It shows detailed information about each position including quantity, average price, current value,
 * profit/loss, and position summary. Users can click on positions to view detailed information
 * in a modal popup.
 * @location Positions tab in the trading terminal
 * @prop apiKey The API key for Groww connector authentication
 * @prop accessToken Optional access token for enhanced authentication
 */

/**
 * @uiWorkflow
 * @name Monitor Active Trading Positions on Groww
 * @description How to track your current trading positions and their performance
 * @platform groww
 * @step 1 Navigate to the trading terminal [component: TerminalTabs] [action: navigate]
 * @step 2 Click on the "Positions" tab to view active positions [component: Positions] [action: click]
 * @step 3 Review the summary showing total positions and investment [component: Positions] [action: observe]
 * @step 4 Check your overall P&L across all positions [component: Positions] [action: observe] [notes: Green indicates profit, red indicates loss]
 * @step 5 Examine individual position details in the table [component: Positions] [action: scroll]
 * @step 6 Click on any position row to view detailed information [component: Positions] [action: click]
 * @step 7 Review product type (CNC/MIS) and exchange information [component: PositionDetailsModal] [action: observe]
 * @step 8 Close the modal by clicking outside or the X button [component: PositionDetailsModal] [action: click]
 */

/**
 * @uiWorkflow
 * @name Analyze Position Performance on Groww
 * @description How to evaluate the performance of your current trading positions
 * @platform groww
 * @step 1 Open the Positions tab in the trading terminal [component: Positions] [action: click]
 * @step 2 Compare your average price with current market value [component: Positions] [action: observe]
 * @step 3 Review the P&L column to see profit or loss per position [component: Positions] [action: observe]
 * @step 4 Check the net quantity to understand your actual exposure [component: Positions] [action: observe]
 * @step 5 Note the product type (CNC for delivery, MIS for intraday) [component: Positions] [action: observe]
 * @step 6 Use this data to decide whether to hold, close, or modify positions [component: Positions] [action: analyze]
 * @step 7 Click on positions for detailed breakdown if needed [component: Positions] [action: click]
 */

// Interface for the modal/popup that shows position details
interface PositionDetailsModalProps {
  position: any;
  onClose: () => void;
}

// Component for showing full position details in a modal
const PositionDetailsModal = ({ position, onClose }: PositionDetailsModalProps) => {
  if (!position) return null;
  
  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modalContent} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <h3>Position Details: {position.trading_symbol}</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>
        <div className={styles.modalBody}>
          <pre>{JSON.stringify(position, null, 2)}</pre>
        </div>
      </div>
    </div>
  );
};

export default function Positions({ apiKey, accessToken }: { apiKey: string, accessToken?: string }) {
  // Use the custom hook to manage positions data
  const { positions, rawPositionData, isLoading, error, fetchPositions } = useGrowwPositions()
  const [selectedPosition, setSelectedPosition] = useState<any>(null)

  // Initialize connector with credentials if needed
  useEffect(() => {
    if (apiKey) {
      // Create and connect the connector if not already connected
      const initializeConnector = async () => {
        const connector = new GrowwConnector(apiKey)
        await connector.connect()
      }
      
      initializeConnector()
    }
  }, [apiKey])

  // Handler for when a position row is clicked
  const handlePositionClick = (symbol: string) => {
    const positionDetails = rawPositionData.find(p => p.trading_symbol === symbol)
    if (positionDetails) {
      setSelectedPosition(positionDetails)
    }
  }
  
  // Close the modal
  const closeModal = () => {
    setSelectedPosition(null)
  }

  if (isLoading) return <div className={styles.loadingState}>Loading positions...</div>
  if (error) return <div className={styles.errorState}>{error}</div>
  if (!positions.length) return <div className={styles.emptyState}>No positions found</div>

  // Calculate total investment and P&L
  const totalInvestment = positions.reduce((sum, position) => 
    sum + (position.quantity * position.averagePrice), 0)
  
  const totalPnL = positions.reduce((sum, position) => sum + position.pnl, 0)

  return (
    <div className={styles.holdingsContainer}>
      <div className={styles.holdingsSummary}>
        <div className={styles.summaryCard}>
          <h3>Total Positions</h3>
          <p>{positions.length}</p>
        </div>
        <div className={styles.summaryCard}>
          <h3>Total Investment</h3>
          <p>₹{totalInvestment.toLocaleString()}</p>
        </div>
        <div className={styles.summaryCard}>
          <h3>Total P&L</h3>
          <p className={totalPnL >= 0 ? styles.profit : styles.loss}>
            ₹{totalPnL.toLocaleString()}
          </p>
        </div>
      </div>
      
      <div className={styles.holdingsTable}>
        <table>
          <thead>
            <tr>
              <th>Symbol</th>
              <th>Quantity</th>
              <th>Average Price</th>
              <th>Current Value</th>
              <th>P&L</th>
              <th>Net Quantity</th>
              <th>Product</th>
              <th>Exchange</th>
            </tr>
          </thead>
          <tbody>
            {positions.map((position, index) => {
              // Find raw position data for additional info
              const rawPosition = rawPositionData[index] || {}
              
              return (
                <tr 
                  key={position.symbol} 
                  className={styles.positionRow}
                  onClick={() => handlePositionClick(position.symbol)}
                >
                  <td>{position.symbol}</td>
                  <td>{position.quantity}</td>
                  <td>₹{position.averagePrice.toLocaleString()}</td>
                  <td>₹{(position.quantity * (position.currentPrice || position.averagePrice)).toLocaleString()}</td>
                  <td className={position.pnl >= 0 ? styles.profit : styles.loss}>
                    ₹{position.pnl.toLocaleString()}
                  </td>
                  <td>{rawPosition.net_carry_forward_quantity || position.quantity}</td>
                  <td>{rawPosition.product || 'CNC'}</td>
                  <td>{rawPosition.exchange || 'NSE'}</td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
      
      {selectedPosition && (
        <PositionDetailsModal position={selectedPosition} onClose={closeModal} />
      )}
    </div>
  )
}
